import { AlertCircle, MailCheck } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";

export interface ForgotPasswordFormProps {
  onSent?: (email: string) => void;
  signInSlot?: ReactNode;
}

export const ForgotPasswordForm = ({ onSent, signInSlot }: ForgotPasswordFormProps) => {
  const client = useUmClient();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const trimmed = email.trim();
    const res = await client.requestPasswordReset({ email: trimmed });
    setLoading(false);
    if (res.ok) {
      setSent(true);
      onSent?.(trimmed);
    } else {
      setError(errorMessage(res.error, "Could not send reset email."));
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1.5">
        <CardTitle className="text-2xl">Reset your password</CardTitle>
        <CardDescription>
          Enter your email and we'll send you a link to choose a new password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {sent ? (
          <Alert variant="success">
            <MailCheck />
            <AlertDescription>
              If an account exists for <strong>{email}</strong>, we've just sent it a
              reset link. Check your inbox.
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={submit} className="grid gap-4" noValidate>
            <Field label="Email" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Button type="submit" className="w-full" loading={loading}>
              Send reset link
            </Button>
            {signInSlot ? (
              <p className="text-center text-sm text-muted-foreground">{signInSlot}</p>
            ) : null}
          </form>
        )}
      </CardContent>
    </Card>
  );
};
